import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Edit3,
  Trash2,
  Key,
  Shield,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Layers,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const PROVIDER_CATALOG = [
  {
    provider: "Anthropic",
    color: "indigo",
    models: [
      { id: "anthropic.claude-opus-4-20250514-v1:0", name: "Claude Opus 4" },
      { id: "anthropic.claude-sonnet-4-5-20251101-v1:0", name: "Claude Sonnet 4.5" },
      { id: "anthropic.claude-sonnet-4-20250514-v1:0", name: "Claude Sonnet 4" },
      { id: "anthropic.claude-haiku-3-5-20241022-v1:0", name: "Claude Haiku 3.5" },
      { id: "anthropic.claude-3-opus-20240229-v1:0", name: "Claude 3 Opus" },
      { id: "anthropic.claude-3-sonnet-20240229-v1:0", name: "Claude 3 Sonnet" },
      { id: "anthropic.claude-3-haiku-20240307-v1:0", name: "Claude 3 Haiku" },
    ]
  },
  {
    provider: "Amazon",
    color: "orange",
    models: [
      { id: "amazon.nova-pro-v1:0", name: "Nova Pro" },
      { id: "amazon.nova-lite-v1:0", name: "Nova Lite" },
      { id: "amazon.nova-micro-v1:0", name: "Nova Micro" },
      { id: "amazon.titan-text-express-v1", name: "Titan Text Express" },
      { id: "amazon.titan-text-lite-v1", name: "Titan Text Lite" },
    ]
  },
  {
    provider: "Meta",
    color: "blue",
    models: [
      { id: "meta.llama3-3-70b-instruct-v1:0", name: "Llama 3.3 70B" },
      { id: "meta.llama3-2-90b-instruct-v1:0", name: "Llama 3.2 90B" },
      { id: "meta.llama3-2-11b-instruct-v1:0", name: "Llama 3.2 11B" },
      { id: "meta.llama3-2-3b-instruct-v1:0", name: "Llama 3.2 3B" },
      { id: "meta.llama3-1-405b-instruct-v1:0", name: "Llama 3.1 405B" },
    ]
  },
  {
    provider: "Mistral AI",
    color: "orange",
    models: [
      { id: "mistral.mistral-large-2402-v1:0", name: "Mistral Large" },
      { id: "mistral.mistral-small-2402-v1:0", name: "Mistral Small" },
      { id: "mistral.mixtral-8x7b-instruct-v0:1", name: "Mixtral 8x7B" },
    ]
  },
  {
    provider: "Cohere",
    color: "green",
    models: [
      { id: "cohere.command-r-plus-v1:0", name: "Command R+" },
      { id: "cohere.command-r-v1:0", name: "Command R" },
      { id: "cohere.command-text-v14", name: "Command Text" },
    ]
  },
  {
    provider: "AI21 Labs",
    color: "purple",
    models: [
      { id: "ai21.jamba-1-5-large-v1:0", name: "Jamba 1.5 Large" },
      { id: "ai21.jamba-1-5-mini-v1:0", name: "Jamba 1.5 Mini" },
    ]
  },
  {
    provider: "GLM (z.ai)",
    color: "teal",
    models: [
      { id: "glm-4-plus", name: "GLM-4 Plus" },
      { id: "glm-4-flash", name: "GLM-4 Flash" },
      { id: "glm-4-air", name: "GLM-4 Air" },
      { id: "glm-z1-preview", name: "GLM-Z1 Preview" },
    ]
  }
];

const ALL_MODEL_IDS = PROVIDER_CATALOG.flatMap(p => p.models.map(m => m.id));

function parseAllowedModels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch (_) { return []; }
}

function getModelName(modelId) {
  for (const p of PROVIDER_CATALOG) {
    const m = p.models.find(m => m.id === modelId);
    if (m) return m.name;
  }
  return modelId;
}

const ModelSelector = ({ selectedModels, onChange }) => {
  const [openProviders, setOpenProviders] = useState({});

  const toggleProvider = (provider) => {
    setOpenProviders(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const toggleModel = (modelId) => {
    if (selectedModels.includes(modelId)) {
      onChange(selectedModels.filter(m => m !== modelId));
    } else {
      onChange([...selectedModels, modelId]);
    }
  };

  const toggleProviderAll = (providerModels, checked) => {
    const ids = providerModels.map(m => m.id);
    if (checked) {
      const merged = [...new Set([...selectedModels, ...ids])];
      onChange(merged);
    } else {
      onChange(selectedModels.filter(m => !ids.includes(m)));
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b border-gray-200">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {selectedModels.length === 0 ? 'All models (unrestricted)' : `${selectedModels.length} model${selectedModels.length !== 1 ? 's' : ''} selected`}
        </span>
        <div className="flex gap-2">
          <button type="button" onClick={() => onChange(ALL_MODEL_IDS)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">All</button>
          <span className="text-gray-300">|</span>
          <button type="button" onClick={() => onChange([])}
            className="text-xs text-gray-500 hover:text-gray-700 font-medium">None</button>
        </div>
      </div>
      {PROVIDER_CATALOG.map(({ provider, models }) => {
        const providerIds = models.map(m => m.id);
        const selectedCount = providerIds.filter(id => selectedModels.includes(id)).length;
        const allSelected = selectedCount === models.length;
        const isOpen = openProviders[provider];

        return (
          <div key={provider} className="border-b border-gray-100 last:border-0">
            <div className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              onClick={() => toggleProvider(provider)}>
              <input type="checkbox" checked={allSelected}
                onChange={(e) => { e.stopPropagation(); toggleProviderAll(models, e.target.checked); }}
                onClick={(e) => e.stopPropagation()}
                ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded mr-2"
              />
              <span className="text-sm font-medium text-gray-700 flex-1">{provider}</span>
              {selectedCount > 0 && (
                <span className="text-xs text-indigo-600 mr-2">{selectedCount}/{models.length}</span>
              )}
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
            </div>
            {isOpen && (
              <div className="bg-gray-50 pb-1">
                {models.map(model => (
                  <label key={model.id} className="flex items-center px-6 py-1.5 hover:bg-gray-100 cursor-pointer">
                    <input type="checkbox" checked={selectedModels.includes(model.id)}
                      onChange={() => toggleModel(model.id)}
                      className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded mr-2"
                    />
                    <span className="text-sm text-gray-600">{model.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [editUserModels, setEditUserModels] = useState([]);

  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    role: 'user'
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users', {
        params: {
          search: searchTerm,
          role: roleFilter === 'all' ? undefined : roleFilter
        }
      });
      setUsers(response.data.data.users);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, roleFilter]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', newUserData);
      toast.success('User created successfully');
      setShowCreateModal(false);
      setNewUserData({
        username: '',
        email: '',
        password: '',
        fullName: '',
        role: 'user'
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUser.id}`, {
        username: selectedUser.username,
        email: selectedUser.email,
        fullName: selectedUser.full_name,
        role: selectedUser.role,
        isActive: selectedUser.is_active,
        allowedModels: editUserModels.length > 0 ? editUserModels : null
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, {
        isActive: !currentStatus
      });
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user status');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, resetPasswordData);
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setResetPasswordData({ newPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditUserModels(parseAllowedModels(user.allowed_models));
    setShowEditModal(true);
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage users and their permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="input-field max-w-xs"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admins</option>
          <option value="user">Users</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">User</th>
                <th className="table-header">Role</th>
                <th className="table-header">Model Access</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created</th>
                <th className="table-header">Last Login</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const userModels = parseAllowedModels(user.allowed_models);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div>
                        <div className="font-medium text-gray-900">{user.username}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        {user.full_name && (
                          <div className="text-sm text-gray-500">{user.full_name}</div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? <Shield className="h-3 w-3 mr-1" /> : <Users className="h-3 w-3 mr-1" />}
                        {user.role}
                      </span>
                    </td>
                    <td className="table-cell">
                      {userModels.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">All models</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {userModels.slice(0, 2).map(modelId => (
                            <span key={modelId} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                              {getModelName(modelId)}
                            </span>
                          ))}
                          {userModels.length > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              +{userModels.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-cell text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="table-cell text-sm text-gray-500">
                      {user.last_login ? formatDate(user.last_login) : 'Never'}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1 rounded text-blue-600 hover:bg-blue-50"
                          title="Edit User & Model Access"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowResetPasswordModal(true);
                          }}
                          className="p-1 rounded text-yellow-600 hover:bg-yellow-50"
                          title="Reset Password"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className={`p-1 rounded ${
                            user.is_active
                              ? 'text-red-600 hover:bg-red-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {user.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          className="p-1 rounded text-red-600 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating a new user.'}
            </p>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New User</h3>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    required
                    value={newUserData.username}
                    onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                    className="input-field"
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                    className="input-field"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={newUserData.password}
                      onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                      className="input-field pr-10"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={newUserData.fullName}
                    onChange={(e) => setNewUserData({...newUserData, fullName: e.target.value})}
                    className="input-field"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                    className="input-field"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewUserData({
                        username: '',
                        email: '',
                        password: '',
                        fullName: '',
                        role: 'user'
                      });
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white mb-10">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={selectedUser.username}
                      onChange={(e) => setSelectedUser({...selectedUser, username: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={selectedUser.role}
                      onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                      className="input-field"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={selectedUser.email}
                    onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={selectedUser.full_name || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, full_name: e.target.value})}
                    className="input-field"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={selectedUser.is_active}
                    onChange={(e) => setSelectedUser({...selectedUser, is_active: e.target.checked})}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>

                {/* Model Access Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="h-4 w-4 text-indigo-600" />
                    <label className="block text-sm font-medium text-gray-700">
                      Allowed Models
                    </label>
                    {editUserModels.length === 0 && (
                      <span className="text-xs text-gray-400">(unrestricted — all models allowed)</span>
                    )}
                  </div>
                  <ModelSelector
                    selectedModels={editUserModels}
                    onChange={setEditUserModels}
                  />
                  <p className="mt-1.5 text-xs text-gray-400">
                    Leave empty to allow all models. Selected models restrict this user's API key access.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedUser(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reset Password for {selectedUser.username}
              </h3>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={resetPasswordData.newPassword}
                      onChange={(e) => setResetPasswordData({newPassword: e.target.value})}
                      className="input-field pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This will reset the user's password and invalidate all their active sessions.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setSelectedUser(null);
                      setResetPasswordData({ newPassword: '' });
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
